import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  BaseStreamType,
  Gender,
  PlayingMode,
  PlaylistSong,
} from '../types.generated';
import { PlayingStateEnum } from '../../enums/PlayerEnums';

interface Preferences {
  artistTypeId: 1 | 2 | null;
  voiceType: Gender | null;
}

interface playerState {
  playerMode: PlayingMode;
  currentPlaylist: PlaylistSong[] | undefined;
  currentPlaylistTitle: string | undefined;
  currentPlaylistId: number | undefined;
  currentPlaylistSongId: number | null;
  currentTrack: BaseStreamType | undefined;
  preferences: Preferences;
  currentBrowseRoute: string;
  playingState: PlayingStateEnum;
  favouriteModeSongId: number | null;
  canAutoPlay: boolean;
  previewCurrentTime: number;
  playPraharAfterPlaylist: boolean;
}

const initialState: playerState = {
  playerMode: PlayingMode.Prahar,
  currentPlaylist: undefined,
  currentPlaylistTitle: undefined,
  currentPlaylistId: undefined,
  currentPlaylistSongId: null,
  currentTrack: undefined,
  playingState: PlayingStateEnum.Pause,
  preferences: {
    artistTypeId: null,
    voiceType: null,
  },
  currentBrowseRoute: '/browse',
  favouriteModeSongId: null,
  canAutoPlay: false,
  previewCurrentTime: 0,
  playPraharAfterPlaylist: true,
};

export const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    setPlayerMode: (state, action: PayloadAction<PlayingMode>) => {
      state.playerMode = action.payload;
    },
    setCurrentPlaylist: (state, action) => {
      const { currentPlaylist } = action.payload;
      state.currentPlaylist = currentPlaylist;
    },
    setCurrentPlaylistTitle: (state, action: PayloadAction<string>) => {
      state.currentPlaylistTitle = action.payload;
    },
    setCurrentPlaylistId: (state, action: PayloadAction<number>) => {
      state.currentPlaylistId = action.payload;
    },
    setCurrentPlaylistSongId: (state, action: PayloadAction<number | null>) => {
      state.currentPlaylistSongId = action.payload;
    },
    setCurrentTrack: (
      state,
      action: PayloadAction<BaseStreamType | undefined>
    ) => {
      state.currentTrack = action.payload;
    },
    toggleFavouriteLocally: (state, action: PayloadAction<number>) => {
      const id = action.payload;

      if (state.currentTrack) {
        // prahar playing & current song favourited
        if (
          state.playerMode === PlayingMode.Prahar &&
          state.currentTrack.song?.id === id
        ) {
          state.currentTrack.song.isFavourited =
            !state.currentTrack.song.isFavourited;
        }
        // prahar playing & any other song favourited
        else if (
          state.playerMode === PlayingMode.Prahar &&
          state.currentTrack.id !== id
        ) {
          // TODO set song as favourite locally when different song is playing
          //this should never happen - skipped
        }
        // playlist playing & current song favourited
        else if (
          state.playerMode === PlayingMode.Playlist &&
          state.currentTrack.id === id
        ) {
          state.currentTrack.song.isFavourited =
            !state.currentTrack.song.isFavourited;
          // TODO set the favourite status of the song in the rendered playlist view
        }
        // playlist playing & any other song favourited
        else if (
          state.playerMode === PlayingMode.Playlist &&
          state.currentTrack.id !== id
        ) {
          // TODO set the favourite state of the song in the rendered playlist view
        }
      }
    },
    setCurrentBrowseRoute: (state, action) => {
      const { currentBrowseRoute } = action.payload;
      state.currentBrowseRoute = currentBrowseRoute;
    },
    setPlayingState: (state, action: PayloadAction<PlayingStateEnum>) => {
      state.playingState = action.payload;
    },
    setFavouriteModeSongId: (state, action: PayloadAction<number>) => {
      state.favouriteModeSongId = action.payload;
    },
    setPreferences: (state, action: PayloadAction<Preferences>) => {
      state.preferences = action.payload;
    },
    setCanAutoplay: (state, action: PayloadAction<boolean>) => {
      state.canAutoPlay = action.payload;
      state.playingState = PlayingStateEnum.Pause;
    },
    setPreviewCurrentTime(state, action: PayloadAction<number>) {
      state.previewCurrentTime = action.payload;
    },
    setPlayPraharAfterPlaylist(state, action: PayloadAction<boolean>) {
      state.playPraharAfterPlaylist = action.payload;
    },
  },
});

export const {
  setPlayerMode,
  setCurrentPlaylist,
  setCurrentPlaylistTitle,
  setCurrentPlaylistId,
  setCurrentPlaylistSongId,
  setCurrentTrack,
  setCurrentBrowseRoute,
  toggleFavouriteLocally,
  setPlayingState,
  setFavouriteModeSongId,
  setPreferences,
  setCanAutoplay,
  setPreviewCurrentTime,
  setPlayPraharAfterPlaylist,
} = playerSlice.actions;

export const currentPlaylistSelector = (state: RootState) =>
  state.player.currentPlaylist;

export const currentPlaylistTitleSelector = (state: RootState) =>
  state.player.currentPlaylistTitle;

export const currentPlaylistIdSelector = (state: RootState) =>
  state.player.currentPlaylistId;

export const currentPlaylistSongIdSelector = (state: RootState) =>
  state.player.currentPlaylistSongId;

export const currentTrackSelector = (state: RootState) =>
  state.player.currentTrack;

export const currentSongSelector = (state: RootState) =>
  (state.player.currentTrack as BaseStreamType)?.song;

export const currentSongUrlSelector = (state: RootState) =>
  (state.player.currentTrack as BaseStreamType)?.song.mediumQuality?.url;

export const currentSongPreviewUrlSelector = (state: RootState) =>
  (state.player.currentTrack as BaseStreamType)?.song.preview?.url;

export const currentSongIdSelector = (state: RootState) =>
  state.player.playerMode === PlayingMode.Playlist ||
  state.player.playerMode === PlayingMode.Favourite
    ? state.player.currentPlaylistSongId
    : (state.player.currentTrack as BaseStreamType)?.song.id;

export const currentTrackIdSelector = (state: RootState) =>
  state.player.currentTrack?.id;

export const playerModeSelector = (state: RootState) => state.player.playerMode;

export const currentBrowseRouteSelector = (state: RootState) =>
  state.player.currentBrowseRoute;

export const playingStateSelector = (state: RootState) =>
  state.player.playingState;

export const favouriteModeSongIdSelector = (state: RootState) =>
  state.player.favouriteModeSongId;

export const preferencesSelector = (state: RootState) =>
  state.player.preferences;

export const canAutoPlaySelector = (state: RootState) =>
  state.player.canAutoPlay;

export const previewCurrentTimeSelector = (state: RootState) =>
  state.player.previewCurrentTime;

export const getPlayPraharAfterPlaylist = (state: RootState) =>
  state.player.playPraharAfterPlaylist;

setPlayPraharAfterPlaylist;

export default playerSlice.reducer;
